import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, LogIn, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If a token is already in storage and not expired, jump straight to /student
  useEffect(() => {
    const token = localStorage.getItem('student_token');
    const exp = parseInt(localStorage.getItem('student_token_exp') || '0', 10);
    if (token && exp && Date.now() < exp) {
      const dest = location.state?.from || '/student';
      navigate(dest, { replace: true });
    }
  }, [navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('İstifadəçi adı və parolu daxil edin');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/student/login`, {
        username: username.trim(),
        password: password.trim(),
      });
      const token = res.data?.token;
      const expiresIn = res.data?.expires_in || 3600;
      if (!token) throw new Error('Token alınmadı');
      localStorage.setItem('student_token', token);
      localStorage.setItem('student_token_exp', String(Date.now() + expiresIn * 1000));
      navigate('/student', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Giriş alınmadı. Yenidən cəhd edin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'radial-gradient(ellipse at center, #1a0a3e 0%, #0a0520 50%, #050510 100%)',
        fontFamily: 'Inter, sans-serif',
      }}
      data-testid="student-login-page"
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'linear-gradient(180deg, rgba(22,16,40,0.95) 0%, rgba(10,5,32,0.95) 100%)',
          border: '1px solid rgba(168,85,247,0.3)',
          borderRadius: 18,
          padding: '32px 28px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 100px rgba(168,85,247,0.15)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div
            style={{
              width: 64,
              height: 64,
              margin: '0 auto 16px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #a855f7 0%, #6d28d9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(168,85,247,0.4)',
            }}
          >
            <GraduationCap size={32} color="#fff" />
          </div>
          <h1
            style={{
              fontFamily: "'Cinzel', Georgia, serif",
              fontSize: 22,
              color: '#f0e68c',
              margin: 0,
              letterSpacing: 0.5,
            }}
          >
            Tələbə girişi
          </h1>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>
            Karma matrisi tələbə portalı
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', display: 'block', marginBottom: 6 }}>
              İstifadəçi adı
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              maxLength={64}
              data-testid="student-login-username"
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                padding: '12px 14px',
                color: '#fff',
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', display: 'block', marginBottom: 6 }}>
              Parol
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              maxLength={128}
              data-testid="student-login-password"
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                padding: '12px 14px',
                color: '#fff',
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {error && (
            <div
              style={{
                fontSize: 12,
                color: '#fca5a5',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                padding: '8px 12px',
                borderRadius: 8,
              }}
              data-testid="student-login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="student-login-submit"
            style={{
              background: 'linear-gradient(135deg, #a855f7 0%, #6d28d9 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '12px 14px',
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginTop: 4,
            }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
            {loading ? 'Yoxlanır...' : 'Giriş'}
          </button>
        </form>

        <div
          style={{
            marginTop: 18,
            paddingTop: 14,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            fontSize: 11,
            color: 'rgba(255,255,255,0.4)',
            textAlign: 'center',
          }}
        >
          Session 1 saat davam edir.{' '}
          <a href="/" style={{ color: '#c4b5fd', textDecoration: 'underline' }}>
            Ana səhifəyə qayıt
          </a>
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;
